// Settings → Reviews tab.
//
// Standalone "Reviews" card for the retailer's outward-facing review
// links. Currently a single field — Google review URL — which surfaces
// in the customer portal's Reviews/Aftercare section as a "Leave a
// Google review" CTA when populated. The CTA is hidden customer-side
// when blank (no generic fallback search per Steve's decision).
//
// Backend wiring:
//   GET  /api/portal/retailer-profile           → { profile.googleReviewUrl }
//   PUT  /api/portal/settings/branding          { googleReviewUrl }
// Reuses the existing branding endpoint to keep one writepoint for
// retailer-profile fields. Server validates: https URL, host must be
// google.com / g.page / goo.gl (or subdomains).

import { ApiError } from "@tkos/api-client";
import { iconMarkup } from "@tkos/shared-ui";

import type { SettingsTab } from "../types";
import type { PageContext } from "../../../shell/types";

import "./reviews.css";

interface ProfileResponse {
  ok: boolean;
  profile?: {
    googleReviewUrl?: string | null;
  };
}

const ALLOWED_HOST_RX = /(^|\.)(google\.com|g\.page|goo\.gl)$/i;

/** Client-side mirror of the server-side validator. Returns the error
 *  string for an invalid URL, or `null` if the URL is empty or valid. */
function validateUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null; // empty clears the field
  let u: URL;
  try {
    u = new URL(trimmed);
  } catch {
    return "Enter a full URL — e.g. https://g.page/r/your-business/review.";
  }
  if (u.protocol !== "https:") {
    return "URL must start with https://";
  }
  if (!ALLOWED_HOST_RX.test(u.hostname)) {
    return "Only google.com, g.page or goo.gl links are accepted.";
  }
  return null;
}

const reviewsTab: SettingsTab = {
  id: "reviews",
  label: "Reviews",
  icon: iconMarkup("star", { size: "sm" }),
  mount(container, ctx: PageContext) {
    const ac = new AbortController();
    let disposed = false;
    let original = "";

    container.innerHTML = `
      <section class="set-reviews-root" aria-labelledby="set-reviews-h">
        <h2 id="set-reviews-h" class="set-section-title">Google reviews</h2>
        <p class="set-help set-reviews-desc">
          Paste the direct link to your Google review page. We'll surface
          a "Leave a Google review" button in your customers' portal once
          their installation is complete. When this field is empty, the
          button is hidden — no generic Google search fallback, so
          customers never land on the wrong listing.
        </p>

        <div class="set-field">
          <label for="set-reviews-url">Google review URL</label>
          <input
            id="set-reviews-url"
            class="set-input"
            type="url"
            inputmode="url"
            autocomplete="off"
            placeholder="https://g.page/r/your-business/review"
            data-role="url"
          />
          <div class="set-help">
            Find your link in Google Business Profile → Customers → Reviews →
            "Get more reviews" → "Share review form". Accepted hosts:
            <code>google.com</code>, <code>g.page</code>, <code>goo.gl</code>.
          </div>
          <p class="set-reviews-error" data-role="error" hidden></p>
        </div>

        <div class="set-reviews-preview" data-role="preview">
          <p class="set-reviews-preview-title">Customer-portal preview</p>
          <p class="set-reviews-preview-empty" data-role="preview-empty">
            No link yet — the customer-portal button stays hidden.
          </p>
          <a
            class="set-reviews-preview-cta"
            data-role="preview-cta"
            href="#"
            target="_blank"
            rel="noopener noreferrer"
            hidden
          >
            ${iconMarkup("star", { size: "sm" })}
            <span>Leave a Google review</span>
          </a>
        </div>

        <div class="set-reviews-actions">
          <button type="button" class="set-btn set-btn-secondary" data-role="reset">Reset</button>
          <button type="button" class="set-btn" data-role="save">Save</button>
        </div>

        <div class="set-ratings" data-role="ratings">
          <h2 class="set-section-title">Customer ratings</h2>
          <p class="set-help">Star ratings your customers leave in their portal. The foundation for your public marketplace profile.</p>
          <div class="set-ratings-summary" data-role="ratings-summary">Loading…</div>
          <ul class="set-ratings-list" data-role="ratings-list"></ul>
        </div>
      </section>
    `;

    const urlInput   = container.querySelector<HTMLInputElement>('[data-role="url"]')!;
    const errEl      = container.querySelector<HTMLParagraphElement>('[data-role="error"]')!;
    const saveBtn    = container.querySelector<HTMLButtonElement>('[data-role="save"]')!;
    const resetBtn   = container.querySelector<HTMLButtonElement>('[data-role="reset"]')!;
    const previewCta = container.querySelector<HTMLAnchorElement>('[data-role="preview-cta"]')!;
    const previewMt  = container.querySelector<HTMLParagraphElement>('[data-role="preview-empty"]')!;

    // ── Customer star ratings (read-only display) ─────────────────
    const escR = (s: string): string =>
      s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
    void (async () => {
      const sumEl = container.querySelector<HTMLElement>('[data-role="ratings-summary"]');
      const listEl = container.querySelector<HTMLElement>('[data-role="ratings-list"]');
      try {
        type Rating = { stars: number; comment?: string; customer_name?: string; created_at: string };
        const r = await ctx.api<{ ok: boolean; average: number; count: number; ratings: Rating[] }>(
          "/portal/ratings", { signal: ac.signal });
        if (disposed) return;
        if (sumEl) {
          sumEl.innerHTML = r.count > 0
            ? `<span class="set-ratings-avg">${r.average.toFixed(1)}</span> ${iconMarkup("star", { size: "sm" })} <span class="set-ratings-count">· ${r.count} rating${r.count === 1 ? "" : "s"}</span>`
            : `<span class="set-ratings-empty">No customer ratings yet.</span>`;
        }
        if (listEl) {
          listEl.innerHTML = r.ratings.map((rt) =>
            `<li class="set-ratings-item">
              <span class="set-ratings-item-stars">${Math.max(1, Math.min(5, rt.stars))}/5</span>
              ${rt.customer_name ? `<span class="set-ratings-item-name">${escR(rt.customer_name)}</span>` : ""}
              ${rt.comment ? `<span class="set-ratings-item-comment">${escR(rt.comment)}</span>` : ""}
            </li>`).join("");
        }
      } catch (err) {
        if (disposed) return;
        if (err instanceof ApiError && err.status === 404) { if (sumEl) sumEl.textContent = ""; return; }
        if (sumEl) sumEl.textContent = "Couldn't load ratings right now.";
      }
    })();

    function showError(msg: string | null): void {
      if (msg) {
        errEl.textContent = msg;
        errEl.hidden = false;
        urlInput.setAttribute("aria-invalid", "true");
      } else {
        errEl.textContent = "";
        errEl.hidden = true;
        urlInput.removeAttribute("aria-invalid");
      }
    }

    function refreshPreview(): void {
      const val = urlInput.value.trim();
      const err = validateUrl(val);
      if (val && !err) {
        previewCta.href = val;
        previewCta.hidden = false;
        previewMt.hidden = true;
      } else {
        previewCta.hidden = true;
        previewMt.hidden = false;
      }
    }

    function onInput(): void {
      const val = urlInput.value;
      showError(validateUrl(val));
      refreshPreview();
    }

    async function load(): Promise<void> {
      try {
        const resp = await ctx.api<ProfileResponse>("/portal/retailer-profile", {
          signal: ac.signal,
        });
        if (disposed) return;
        original = (resp?.profile?.googleReviewUrl ?? "").trim();
        urlInput.value = original;
        refreshPreview();
      } catch (err) {
        if ((err as DOMException)?.name === "AbortError") return;
        ctx.showToast("Couldn't load reviews settings", "error");
      }
    }

    async function onSave(): Promise<void> {
      const val = urlInput.value.trim();
      const err = validateUrl(val);
      if (err) {
        showError(err);
        urlInput.focus();
        return;
      }
      saveBtn.disabled = true;
      try {
        await ctx.api("/portal/settings/branding", {
          method: "PUT",
          signal: ac.signal,
          body: JSON.stringify({ googleReviewUrl: val }),
        });
        if (disposed) return;
        original = val;
        ctx.showToast(val ? "Google review link saved" : "Google review link cleared", "success");
      } catch (err2) {
        if ((err2 as DOMException)?.name === "AbortError") return;
        const msg = err2 instanceof ApiError && err2.message
          ? err2.message
          : "Couldn't save Google review link";
        ctx.showToast(msg, "error");
      } finally {
        if (!disposed) saveBtn.disabled = false;
      }
    }

    function onReset(): void {
      urlInput.value = original;
      showError(null);
      refreshPreview();
    }

    urlInput.addEventListener("input", onInput);
    saveBtn.addEventListener("click", () => { void onSave(); });
    resetBtn.addEventListener("click", onReset);

    void load();

    return () => {
      disposed = true;
      ac.abort();
      urlInput.removeEventListener("input", onInput);
    };
  },
};

export default reviewsTab;
